import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { LinkProps } from "@tanstack/react-router";
import { cx } from "@/lib/format";

/**
 * The button.
 *
 * Three weights and one accent. The primary action is the brand blue with a
 * coloured shadow under it and a one-pixel lift on hover — the only place in
 * the product where a shadow is used to make something look raised rather
 * than to say it floats. Secondary is a white surface with a hairline.
 * Ghost has no chrome until the pointer arrives.
 *
 * Destructive actions are a ghost that turns red, not a red fill: a filled
 * red button sits in the page shouting for as long as the page is open, and
 * the thing it does only matters at the moment it is pressed.
 */
export type ButtonTone = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "dense" | "control" | "unit";

const SIZES: Record<ButtonSize, string> = {
  dense: "h-8 px-3 text-xs gap-1.5 rounded-lg",
  control: "h-[38px] px-4 text-sm gap-2 rounded-lg",
  unit: "h-11 px-5 text-sm gap-2 rounded-lg",
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: ButtonTone;
  size?: ButtonSize;
  busy?: boolean;
  wide?: boolean;
};

/**
 * What a tone looks like.
 *
 * Lifted out of the component so a link can wear it. A control that navigates
 * has to be an <a> — it is middle-clickable, it has a URL in the status bar,
 * and a <button> with a router call in its onClick is none of those things —
 * but it should be indistinguishable from a button when it sits beside one.
 * One table, two elements.
 */
function toneClasses(tone: ButtonTone): string {
  const tones: Record<ButtonTone, string> = {
    primary: cx(
      "border border-transparent font-[550] text-[var(--on-accent)]",
      "bg-[var(--primary)] shadow-[var(--shadow-primary)]",
      "hover:bg-[var(--primary-hover)] hover:-translate-y-px hover:shadow-[var(--shadow-primary-hover)]",
      "active:translate-y-0",
    ),
    secondary: cx(
      "border border-[var(--border-strong)] font-[550] text-[var(--text-primary)]",
      "bg-[var(--surface)] shadow-[var(--shadow-sm)]",
      "hover:bg-[var(--overlay-soft)] hover:shadow-[var(--shadow-md)]",
    ),
    ghost: cx(
      "border border-transparent font-medium text-[var(--text-secondary)]",
      "hover:bg-[var(--overlay-soft)] hover:text-[var(--text-primary)]",
    ),
    danger: cx(
      "border border-transparent font-[550] text-[var(--danger)]",
      "hover:bg-[var(--danger-soft)]",
    ),
  };
  return tones[tone];
}

const BASE =
  "press inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap select-none";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { tone = "secondary", size = "control", busy, wide, className, children, disabled, style, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      style={style}
      className={cx(
        BASE,
        "disabled:pointer-events-none disabled:opacity-50",
        toneClasses(tone),
        SIZES[size],
        wide && "w-full",
        className,
      )}
      {...rest}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
});

/**
 * A button that is a link.
 *
 * For a control whose whole job is to go somewhere: "Order capacity" on the
 * billing screen, "Billing" on the ordering one. Typed by the router, so a
 * path that does not exist is a build error rather than a 404 somebody finds.
 */
export function ButtonLink({
  tone = "secondary",
  size = "control",
  wide,
  className,
  children,
  ...rest
}: LinkProps & {
  tone?: ButtonTone;
  size?: ButtonSize;
  wide?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <Link
      {...rest}
      className={cx(BASE, toneClasses(tone), SIZES[size], wide && "w-full", className)}
    >
      {children}
    </Link>
  );
}

/**
 * An icon alone.
 *
 * `label` is required rather than optional: an icon button with no
 * accessible name is a button that does not exist for a screen reader, and
 * the type system is a better place to catch that than a review.
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string;
    tone?: Extract<ButtonTone, "ghost" | "secondary">;
    size?: "dense" | "control";
    children: ReactNode;
  }
>(function IconButton({ label, tone = "ghost", size = "control", className, children, ...rest }, ref) {
  return (
    <button
      ref={ref}
      aria-label={label}
      title={label}
      className={cx(
        "press inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg",
        size === "dense" ? "h-8 w-8" : "h-[38px] w-[38px]",
        tone === "ghost"
          ? "text-[var(--text-muted)] hover:bg-[var(--overlay-soft)] hover:text-[var(--text-primary)]"
          : "border border-[var(--border)] bg-[var(--surface)] text-[var(--text-secondary)] hover:bg-[var(--overlay-soft)]",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/** Sized to the line it sits on, so a button keeps its width while working. */
export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        "animate-turn inline-block h-3.5 w-3.5 shrink-0 rounded-full",
        "border-[1.5px] border-current border-t-transparent",
        className,
      )}
      aria-hidden
    />
  );
}
